import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { DataTable, SearchFilterBar, type ColumnDef, type FilterDef, type SortState } from '@shared-ui/components';
import { type UserSearchOption } from '@shared-ui/user-picker';
import { ScopedUserSearchFilter } from '../../components/ScopedUserSearchFilter';
import { adminApi } from '../../api/adminApi';
import { DriveBackupDetail } from './DriveBackupDetail';
import { formatWhen, stateOf, type BackupRow } from './driveBackupFormat';
import { BackupStateBadge } from './BackupStateBadge';
import './DriveBackups.css';

const STATE_OPTIONS = [
  { value: 'needs-attention', label: 'Needs attention' },
  { value: 'active', label: 'Active' },
  { value: 'stalled', label: 'Not sweeping' },
  { value: 'paused', label: 'Paused (Drive full)' },
  { value: 'disconnected', label: 'Access revoked' },
  { value: 'idle', label: 'Nothing delivered yet' },
];

/**
 * Free-text match across the three things a person searches this table by: who
 * the user is, and which Google account their work is going to.
 *
 * The Google account is the one most worth searching. A support question
 * arrives as "vink330@gmail.com says their backup stopped", and that address is
 * often NOT the account they log in with — so matching only our own user email
 * would miss exactly the case somebody came here to answer.
 */
function matchesSearch(row: BackupRow, q: string): boolean {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  return [row.name, row.email, row.accountEmail, row.userId]
    .some(v => v?.toLowerCase().includes(needle));
}

function matchesFilter(row: BackupRow, state: string): boolean {
  if (!state) return true;
  // "Needs attention" is the default view: everything a person would actually
  // open this tab to find, in one filter rather than three.
  if (state === 'needs-attention') return stateOf(row) !== 'active';
  if (state === 'idle') return row.deliveredCount === 0;
  return stateOf(row) === state;
}

/**
 * Who is backing their work up to their own cloud storage, and whether it is
 * still working.
 *
 * The column that earns this tab is "Not sweeping": a connection still marked
 * active whose sweep has not run in hours is a backup that has quietly stopped,
 * which `status` alone reports as healthy and nothing else in the product would
 * surface. The default filter is therefore "needs attention" rather than
 * everyone — a healthy backup is the one nobody has to look at.
 *
 * Reads the user-dyninfo projection, never the connection documents: those hold
 * sealed refresh tokens and an admin screen has no business loading one to
 * render a status.
 *
 * See docs/FeatureDesigns/data-ownership/personal-drive-backup-target.md.
 */
export function DriveBackups() {
  const [state, setState] = useState('');
  const [search, setSearch] = useState('');
  // Kept as the picker's own value (an email) rather than a userId: the control
  // is keyed on email, and the row carries one to match against.
  const [pickedEmail, setPickedEmail] = useState('');
  // The open connection lives in the URL, so a drilled-in view survives a
  // reload and the browser's back button returns to the list.
  const [searchParams, setSearchParams] = useSearchParams();
  const openOwner = searchParams.get('backupOwner');
  const openConnection = (ownerId: string | null) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('backupView');
      next.delete('backupPath');
      if (ownerId) next.set('backupOwner', ownerId); else next.delete('backupOwner');
      return next;
    });
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['driveBackups'],
    queryFn: () => adminApi.getDriveBackups(),
  });

  const all = useMemo(() => data?.backups ?? [], [data]);
  const rows = useMemo(() => all
    .filter(r => matchesFilter(r, state))
    .filter(r => matchesSearch(r, search))
    // The picker narrows to one person exactly; free text stays fuzzy.
    .filter(r => !pickedEmail || r.email === pickedEmail),
  [all, state, search, pickedEmail]);

  const counts = useMemo(() => ({
    total: all.length,
    stalled: all.filter(r => stateOf(r) === 'stalled').length,
    paused: all.filter(r => stateOf(r) === 'paused').length,
    disconnected: all.filter(r => stateOf(r) === 'disconnected').length,
    files: all.reduce((n, r) => n + r.deliveredCount, 0),
  }), [all]);

  const [sort, setSort] = useState<SortState>({ sortBy: 'state', sortOrder: 'asc' });

  const filterDefs: FilterDef[] = [
    { key: 'state', kind: 'select', placeholder: 'All states', options: STATE_OPTIONS },
  ];

  if (openOwner) {
    return (
      <DriveBackupDetail
        ownerId={openOwner}
        backup={all.find(r => r.userId === openOwner)}
        onBack={() => openConnection(null)}
      />
    );
  }

  const columns: ColumnDef<BackupRow>[] = [
    {
      key: 'user', header: 'User', sortable: true, mobile: 'title',
      sortValue: r => (r.name || r.email || r.userId).toLowerCase(),
      render: r => (
        <div className="db-user">
          <span>{r.name || '—'}</span>
          {r.email && <span className="db-user-sub">{r.email}</span>}
        </div>
      ),
    },
    {
      key: 'account', header: 'Google account', sortable: true, mobile: 'meta',
      sortValue: r => (r.accountEmail ?? '').toLowerCase(),
      render: r => r.accountEmail ?? '—',
    },
    {
      key: 'state', header: 'State', sortable: true, mobile: 'accessory',
      // Ranked, not alphabetical: sorting by this column should bring the
      // broken ones together at one end, which "active/disconnected/paused"
      // as text would not.
      sortValue: r => ({ disconnected: 0, paused: 1, stalled: 2, active: 3 })[stateOf(r)],
      render: r => <BackupStateBadge row={r} />,
    },
    {
      key: 'files', header: 'Files', sortable: true, mobile: 'meta',
      sortValue: r => r.deliveredCount,
      render: r => r.deliveredCount,
    },
    {
      key: 'lastDelivered', header: 'Last delivered', sortable: true, mobile: 'meta',
      sortValue: r => r.lastDeliveredAt ?? '',
      render: r => formatWhen(r.lastDeliveredAt),
    },
    {
      key: 'lastSwept', header: 'Last swept', sortable: true, mobile: 'meta',
      sortValue: r => r.lastSweepAt ?? '',
      render: r => formatWhen(r.lastSweepAt),
    },
    {
      key: 'connected', header: 'Connected', sortable: true, mobile: 'hide',
      sortValue: r => r.connectedAt,
      render: r => new Date(r.connectedAt).toLocaleDateString(),
    },
  ];

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>Drive Backups</h2>
      </div>

      <div className="stat-cards stat-cards--5col db-stats">
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{counts.total}</div>
          <div className="stat-card-label">Connected</div>
        </div>
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{counts.files}</div>
          <div className="stat-card-label">Files delivered</div>
        </div>
        {/* Three separate cards rather than one "problems" total: they need
            different actions — a full Drive is the user's to clear, a revoked
            grant needs them to reconnect, and a stalled sweep is ours. */}
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{counts.stalled}</div>
          <div className="stat-card-label">Not sweeping</div>
        </div>
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{counts.paused}</div>
          <div className="stat-card-label">Drive full</div>
        </div>
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{counts.disconnected}</div>
          <div className="stat-card-label">Access revoked</div>
        </div>
      </div>

      <SearchFilterBar
        filters={filterDefs}
        values={{ state, search }}
        onChange={values => {
          setState(values.state ?? '');
          setSearch(values.search ?? '');
        }}
        onFetch={() => refetch()}
        loading={isLoading}
        searchPlaceholder="Google account, name or email…"
        leading={(
          <ScopedUserSearchFilter
            value={pickedEmail}
            onChange={setPickedEmail}
            onPick={(o: UserSearchOption | null) => setPickedEmail(o?.email ?? '')}
            placeholder="User name or email…"
          />
        )}
      />

      {error && <p className="error">{error instanceof Error ? error.message : 'Failed to load'}</p>}

      <DataTable<BackupRow>
        columns={columns}
        data={rows}
        rowKey={r => r.userId}
        onRowClick={r => openConnection(r.userId)}
        sort={sort}
        onSortChange={setSort}
        compact
        bordered
        loading={isLoading}
        emptyMessage={all.length === 0
          ? 'Nobody has connected a Drive yet.'
          : 'No backups match this filter.'}
      />
    </div>
  );
}
